import { logger } from "@/lib/logger";
import { supabase } from "@/integrations/supabase/client";

export interface AuditLogEntry {
  action: string;
  entityType: string;
  entityId?: string;
  details: Record<string, any>;
  userId?: string;
  /** 21 CFR Part 11: Previous values before the change (for compliance) */
  previousValues?: Record<string, any>;
}

/**
 * Log an audit trail entry with optional previous values for 21 CFR Part 11 compliance.
 * The previous_values field captures the state before changes for regulatory audit trails.
 */
export async function logAuditTrail(entry: AuditLogEntry) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from("audit_trails")
      .insert([
        {
          user_id: entry.userId || user?.id,
          action: entry.action,
          entity_type: entry.entityType,
          entity_id: entry.entityId,
          details: entry.details,
          previous_values: entry.previousValues || null,
        },
      ]);

    if (error) {
      logger.error("Failed to log audit trail:", error);
    }
  } catch (error) {
    logger.error("Audit logging error:", error);
  }
}

// Wrapper functions for common actions
export const auditLog = {
  visitScheduled: (visitId: string, patientId: string, details: any) =>
    logAuditTrail({
      action: "visit_scheduled",
      entityType: "patient_visits",
      entityId: visitId,
      details: { patientId, ...details },
    }),

  visitCompleted: (visitId: string, details: any) =>
    logAuditTrail({
      action: "visit_completed",
      entityType: "patient_visits",
      entityId: visitId,
      details,
    }),

  vitalsRecorded: (vitalId: string, patientId: string, vitalType: string) =>
    logAuditTrail({
      action: "vitals_recorded",
      entityType: "patient_vitals",
      entityId: vitalId,
      details: { patientId, vitalType },
    }),

  labResultAdded: (labId: string, patientId: string, testName: string) =>
    logAuditTrail({
      action: "lab_result_added",
      entityType: "patient_lab_results",
      entityId: labId,
      details: { patientId, testName },
    }),

  adverseEventLogged: (aeId: string, patientId: string, severity: string) =>
    logAuditTrail({
      action: "adverse_event_logged",
      entityType: "adverse_events",
      entityId: aeId,
      details: { patientId, severity },
    }),

  patientStatusChanged: (patientId: string, oldStatus: string, newStatus: string) =>
    logAuditTrail({
      action: "patient_status_changed",
      entityType: "patient_trial_site_assignments",
      entityId: patientId,
      details: { oldStatus, newStatus },
    }),

  assessmentCompleted: (assessmentId: string, patientId: string, type: string) =>
    logAuditTrail({
      action: "assessment_completed",
      entityType: "patient_assessments",
      entityId: assessmentId,
      details: { patientId, type },
    }),

  // Ecosystem audit actions
  staffAssigned: (staffId: string, siteId: string, userId: string, role: string) =>
    logAuditTrail({
      action: "staff_assigned",
      entityType: "trial_site_staff",
      entityId: staffId,
      details: { siteId, userId, role },
    }),

  staffRoleChanged: (staffId: string, siteId: string, oldRole: string, newRole: string) =>
    logAuditTrail({
      action: "staff_role_changed",
      entityType: "trial_site_staff",
      entityId: staffId,
      details: { siteId, oldRole, newRole },
    }),

  affiliationRequested: (affiliationId: string, professionalId: string, siteId: string) =>
    logAuditTrail({
      action: "affiliation_requested",
      entityType: "professional_site_affiliation",
      entityId: affiliationId,
      details: { professionalId, siteId },
    }),

  affiliationApproved: (affiliationId: string, professionalId: string, siteId: string) =>
    logAuditTrail({
      action: "affiliation_approved",
      entityType: "professional_site_affiliation",
      entityId: affiliationId,
      details: { professionalId, siteId },
    }),

  organizationMemberAdded: (memberId: string, userId: string, organizationId: string, role: string) =>
    logAuditTrail({
      action: "organization_member_added",
      entityType: "organization_member",
      entityId: memberId,
      details: { userId, organizationId, role },
    }),

  trialSiteCreated: (siteId: string, organizationId: string, siteName: string) =>
    logAuditTrail({
      action: "trial_site_created",
      entityType: "trial_site",
      entityId: siteId,
      details: { organizationId, siteName },
    }),

  credentialUploaded: (credentialId: string, userId: string, credentialType: string) =>
    logAuditTrail({
      action: "credential_uploaded",
      entityType: "professional_credential",
      entityId: credentialId,
      details: { userId, credentialType },
    }),

  credentialVerified: (credentialId: string, userId: string, verifiedBy: string) =>
    logAuditTrail({
      action: "credential_verified",
      entityType: "professional_credential",
      entityId: credentialId,
      details: { userId, verifiedBy },
    }),

  consentUpdated: (patientId: string, newValues: any, previousValues: any) =>
    logAuditTrail({
      action: "consent_updated",
      entityType: "consent_attestations",
      entityId: patientId,
      details: newValues,
      previousValues,
    }),

  patientActivated: (patientId: string, activatedBy: string, reason: string) =>
    logAuditTrail({
      action: "patient_manually_activated",
      entityType: "profiles",
      entityId: patientId,
      details: { activatedBy, reason, activatedAt: new Date().toISOString() },
    }),

  // Reimbursement compliance (21 CFR Part 11)
  reimbursementExported: (
    format: "csv" | "pdf",
    scope: { trialId?: string | null; siteId?: string | null; patientId?: string | null; sponsor?: string | null },
    summary: { recordCount: number; totalPaid: number; totalApproved: number; totalPending: number; currency: string }
  ) =>
    logAuditTrail({
      action: "reimbursement_exported",
      entityType: "patient_stipends",
      entityId: scope.patientId || scope.siteId || undefined,
      details: {
        format,
        scope,
        summary,
        exportedAt: new Date().toISOString(),
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      },
    }),

  reimbursementStatusChanged: (
    stipendId: string,
    oldStatus: string,
    newStatus: string,
    context: {
      patientId?: string;
      siteId?: string;
      trialId?: string | null;
      amount?: number;
      currency?: string;
      reason?: string;
      /** Full prior snapshot of mutable stipend fields (for before→after diff). */
      previous?: {
        status?: string | null;
        amount?: number | null;
        currency?: string | null;
        approved_by?: string | null;
        approved_at?: string | null;
        paid_by?: string | null;
        paid_at?: string | null;
      };
      /** Full next snapshot of the same fields after the update. */
      next?: {
        status?: string | null;
        amount?: number | null;
        currency?: string | null;
        approved_by?: string | null;
        approved_at?: string | null;
        paid_by?: string | null;
        paid_at?: string | null;
      };
    } = {}
  ) => {
    const { previous, next, ...rest } = context;
    return logAuditTrail({
      action: "reimbursement_status_changed",
      entityType: "patient_stipends",
      entityId: stipendId,
      details: {
        oldStatus,
        newStatus,
        ...rest,
        next: next || { status: newStatus },
        changedAt: new Date().toISOString(),
      },
      previousValues: previous || { status: oldStatus },
    });
  },
};
